import type { Case } from "../../services/cases.service";
import {
  getLabTodayDateOnly,
  parseDateOnly,
  requireDateOnlyString,
  type DateOnlyString,
} from "../../utils/dateOnly";

type AccessioningCaseHydrationSource = Pick<
  Case,
  | "clinicalHistoryCodes"
  | "consultationReason"
  | "consultationOriginalDiagnosis"
  | "consultationClinicalContext"
  | "requestingPathologist"
  | "originatingPracticeId"
  | "originatingPhysicianId"
  | "originatingSurgeryCenterId"
>;

function getPersistedSpecializedCaseValues(
  caseData: AccessioningCaseHydrationSource
) {
  return {
    clinicalHistoryCodes: caseData.clinicalHistoryCodes || [],
    consultationReason: caseData.consultationReason || "",
    consultationOriginalDiagnosis: caseData.consultationOriginalDiagnosis || "",
    consultationClinicalContext: caseData.consultationClinicalContext || "",
    requestingPathologist: caseData.requestingPathologist || "",
    originatingPracticeId: caseData.originatingPracticeId || "",
    originatingPhysicianId: caseData.originatingPhysicianId || "",
    originatingSurgeryCenterId: caseData.originatingSurgeryCenterId || "",
  };
}

type SpecializedCaseValues = ReturnType<
  typeof getPersistedSpecializedCaseValues
>;

type SetSpecializedCaseFieldValue = (
  field: `case.${keyof SpecializedCaseValues}`,
  value: SpecializedCaseValues[keyof SpecializedCaseValues]
) => void;

export function hydratePersistedSpecializedCaseFields(
  caseData: AccessioningCaseHydrationSource,
  setFieldValue: SetSpecializedCaseFieldValue
) {
  const values = getPersistedSpecializedCaseValues(caseData);
  const fields = Object.keys(values) as Array<keyof SpecializedCaseValues>;

  fields.forEach((field) => setFieldValue(`case.${field}`, values[field]));
}

export function normalizeAccessioningDateOnly(
  value: unknown
): DateOnlyString | null {
  return parseDateOnly(value) ?? null;
}

export function getNewAccessioningDateReceived(
  now: Date = new Date()
): DateOnlyString {
  return getLabTodayDateOnly(now);
}

export function getAccessioningStickerDateReceived(
  value: unknown
): DateOnlyString {
  return requireDateOnlyString(value, "Date received");
}
